import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { DatePicker } from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";
import { FaClipboardList, FaUser } from "react-icons/fa";
import DataParser from "../../../utlits/Dataparser";
import CrmLeadsServices from "../../../Services/CrmLeadsServices";

// ── Colours ───────────────────────────────────────────────────────────────────
const TEAL = "#0DDFAB";
const DARK = "#1e293b";

const STAGE_COLORS = {
  new:       "#0DDFAB",
  contacted: "#6366F1",
  qualified: "#F59E0B",
  proposal:  "#3B82F6",
  won:       "#10B981",
};

const LOST_COLORS = ["#EF4444", "#F59E0B", "#6366F1", "#3B82F6", "#10B981", "#8B5CF6"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDays = (d) => (!d || d === 0 ? "—" : `${d}d`);
const dayColor = (d) => {
  if (d <= 1) return "#10B981";
  if (d <= 5) return "#F59E0B";
  return "#EF4444";
};

// ── Reusable pieces ───────────────────────────────────────────────────────────
const Card = ({ title, children, style = {} }) => (
  <div style={{
    background: "#F7F8FA",
    borderRadius: 14,
    padding: "20px 22px",
    boxShadow: "none",
    border: "none",
    ...style
  }}>
    {title && (
      <p style={{ margin: "0 0 16px", fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>{title}</p>
    )}
    {children}
  </div>
);

const HBar = ({ pct, color = TEAL, height = 8 }) => (
  <div style={{ background: "#dde3ea", borderRadius: 99, overflow: "hidden", height }}>
    <div style={{ width: `${Math.max(pct, pct > 0 ? 3 : 0)}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.5s ease" }} />
  </div>
);

const BarSkeleton = ({ rows = 4 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <Skeleton width={80} height={12} />
          <Skeleton width={35} height={12} />
        </div>
        <Skeleton height={8} borderRadius={99} />
      </div>
    ))}
  </>
);

// Exact chip design matching CrmLeads listing page
const StatChip = ({ label, value, iconType, iconSrc, IconComponent, loading }) => (
  <div style={{
    background: "#fff", border: "1px solid #0DDFAB", borderRadius: "10px",
    padding: "6px 10px", display: "flex", flexDirection: "column",
    alignItems: "flex-start", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", minWidth: 0
  }}>
    <div style={{
      width: 26, height: 26, borderRadius: 6, background: "#d1faf0",
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 2, flexShrink: 0
    }}>
      {iconType === "icon"
        ? <IconComponent style={{ fontSize: 14, color: "#0a9e7a" }} />
        : <img src={iconSrc} alt="" style={{ width: 15, height: 15, filter: "brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(394%) hue-rotate(118deg) brightness(91%) contrast(95%)" }} />
      }
    </div>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", width: "100%", gap: 4 }}>
      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 700, color: "#1a2332", lineHeight: 1, flexShrink: 0 }}>
        {loading ? <Skeleton width={20} /> : String(value ?? 0).padStart(2, "0")}
      </span>
    </div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const CrmDashboard = () => {
  const companyId = DataParser.CompanyId();
  const isAdmin   = (DataParser.userRoleTitle() || "").toLowerCase() === "company admin";

  // Default date range: start of current month → today
  const [dateRange, setDateRange] = useState([moment().startOf("month"), moment()]);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchDashboard = useCallback(async (range) => {
    const [start, end] = range || dateRange;
    setLoading(true);
    setError(null);
    try {
      const res = await CrmLeadsServices.getDashboard(
        companyId,
        start.format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD")
      );
      if (res?.Good) {
        setData(res.data);
      } else {
        setError("Failed to load dashboard data.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Fetch on mount and whenever date range changes
  useEffect(() => { fetchDashboard(dateRange); }, [dateRange[0]?.valueOf(), dateRange[1]?.valueOf()]);

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else if (!dates) {
      // Cleared — reset to current month
      setDateRange([moment().startOf("month"), moment()]);
    }
  };

  const maxFunnelCount = data?.funnel?.length
    ? Math.max(...data.funnel.map(f => f.count), 1)
    : 1;

  // ── Access guard ──────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
              <p style={{ fontSize: 16, color: DARK, fontWeight: 600 }}>Dashboard is restricted to Company Admins.</p>
              <p style={{ fontSize: 13, color: "#64748b" }}>Contact your administrator to get access.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content" style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
        <div className="container-fluid">
      <Helmet><title>Lead Dashboard | Planit</title></Helmet>

      {/* Scoped CSS — identical technique to ActionQueue */}
      <style>{`
        .crm-dash-filters .task-date-range-wrap { position: relative; }
        .crm-dash-filters .task-date-range-wrap::before,
        .crm-dash-filters .task-date-range-wrap::after {
          position: absolute; top: 0; transform: translateY(-50%);
          font-size: 11px; font-weight: 600; color: #D2D2D2;
          background: #fff; padding: 0 3px; z-index: 10;
          pointer-events: none; opacity: 0; transition: color 0.2s, opacity 0.2s;
        }
        .crm-dash-filters .task-date-range-wrap::before { content: 'Start Date'; left: 12px; }
        .crm-dash-filters .task-date-range-wrap::after  { content: 'End Date';   left: calc(50% + 8px); }
        .crm-dash-filters .task-date-range-wrap.has-value::before  { opacity: 1; color: #1D1D1D; }
        .crm-dash-filters .task-date-range-wrap.has-end-value::after { opacity: 1; color: #1D1D1D; }
        .crm-dash-filters .task-date-range-wrap .ant-picker {
          height: 34px !important; min-height: 34px !important; border-radius: 6px !important;
          border: 1.5px solid #F2F2F2 !important; font-size: 11px !important; padding: 0 10px !important;
        }
        .crm-dash-filters .task-date-range-wrap:hover .ant-picker,
        .crm-dash-filters .task-date-range-wrap .ant-picker-focused { border-color: #1D1D1D !important; box-shadow: none !important; }
        .crm-dash-filters .task-date-range-wrap .ant-picker-input > input { font-size: 11px !important; font-weight: 300 !important; }
        .crm-dash-filters .task-date-range-wrap .ant-picker-separator { font-size: 11px !important; }
      `}</style>

      {/* ── Date range filter — top right ── */}
      <div className="d-flex align-items-center justify-content-end mb-3 px-3 mt-3">
        <div className="crm-dash-filters" style={{ display: 'flex', alignItems: 'center' }}>
          <div className={`task-date-range-wrap ${dateRange?.[0] ? 'has-value' : ''} ${dateRange?.[1] ? 'has-end-value' : ''}`}
            style={{ width: 270 }}>
            <DatePicker.RangePicker
              className="w-100"
              format="DD-MM-YYYY"
              allowClear
              value={dateRange}
              onChange={handleRangeChange}
              placeholder={['Start Date', 'End Date']}
              separator="→"
              suffixIcon={
                <div style={{ backgroundColor: '#F4F4F4', borderRadius: '5px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '-4px' }}>
                  <i className="uil-schedule" style={{ color: '#888', fontSize: '14px' }} />
                </div>
              }
              presets={[
                { label: 'This Month',   value: [moment().startOf('month'),   moment()] },
                { label: 'Last Month',   value: [moment().subtract(1,'month').startOf('month'), moment().subtract(1,'month').endOf('month')] },
                { label: 'This Quarter', value: [moment().startOf('quarter'), moment()] },
                { label: 'Last Quarter', value: [moment().subtract(1,'quarter').startOf('quarter'), moment().subtract(1,'quarter').endOf('quarter')] },
                { label: 'Last 30 Days', value: [moment().subtract(29,'days'), moment()] },
                { label: 'Last 90 Days', value: [moment().subtract(89,'days'), moment()] },
                { label: 'This Year',    value: [moment().startOf('year'),    moment()] },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Summary chips ── */}
      <div className="px-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <StatChip label="Total Leads"  value={data?.summary?.totalLeads}  iconType="icon" IconComponent={FaClipboardList} loading={loading} />
        <StatChip label="Active"       value={data?.summary?.totalActive} iconType="icon" IconComponent={FaUser}          loading={loading} />
        <StatChip label="Won"          value={data?.summary?.totalWon}    iconType="svg"  iconSrc="/assets/images/won.svg" loading={loading} />
        <StatChip label="Lost"         value={data?.summary?.totalLost}   iconType="svg"  iconSrc="/assets/images/lost.svg" loading={loading} />
      </div>

      {/* ── Cards grid ── */}
      <div className="px-3" style={{ paddingTop: 12, paddingBottom: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

        {/* FUNNEL */}
        <Card title="Funnel">
          {loading ? <BarSkeleton rows={5} /> : error ? (
            <p style={{ color: "#EF4444", fontSize: 13 }}>{error}</p>
          ) : data?.funnel?.length ? (
            data.funnel.map(item => (
              <div key={item.stage} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: DARK, display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: STAGE_COLORS[item.stage] || TEAL, flexShrink: 0, display: "inline-block" }} />
                    {item.label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: STAGE_COLORS[item.stage] || TEAL }}>{item.count}</span>
                </div>
                <HBar pct={maxFunnelCount > 0 ? (item.count / maxFunnelCount) * 100 : 0} color={STAGE_COLORS[item.stage] || TEAL} height={9} />
              </div>
            ))
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No leads created in this date range.</p>
          )}
        </Card>

        {/* LOST REASONS */}
        <Card title="Lost Reasons">
          {loading ? <BarSkeleton rows={4} /> : data?.lostReasons?.length ? (
            data.lostReasons.map((item, idx) => (
              <div key={item.reason} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: DARK, display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: LOST_COLORS[idx % LOST_COLORS.length], flexShrink: 0, display: "inline-block" }} />
                    {item.label}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                    {item.pct}%
                    <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 4, fontSize: 11 }}>({item.count})</span>
                  </span>
                </div>
                <HBar pct={item.pct} color={LOST_COLORS[idx % LOST_COLORS.length]} height={8} />
              </div>
            ))
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No lost leads in this date range.</p>
          )}
        </Card>

        {/* CONVERSION BY SOURCE */}
        <Card title="Conversion by Source">
          {loading ? <BarSkeleton rows={4} /> : data?.sourceConversion?.length ? (
            data.sourceConversion.map(item => (
              <div key={item.sourceId} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: DARK, maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", flexShrink: 0 }}>
                    {item.pct}%
                    <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 4, fontSize: 11 }}>({item.won}/{item.total})</span>
                  </span>
                </div>
                <HBar pct={item.pct} color={TEAL} height={8} />
              </div>
            ))
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No source data in this date range.</p>
          )}
        </Card>

        {/* AVG TIME IN STAGE */}
        <Card title="Avg Time in Stage">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                <Skeleton width={120} height={14} />
                <Skeleton width={45} height={14} />
              </div>
            ))
          ) : data?.avgTimeInStage?.length ? (
            <>
              {data.avgTimeInStage.map((item, idx) => (
                <div key={item.stage} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "13px 0",
                  borderBottom: idx < data.avgTimeInStage.length - 1 ? "1px solid #f1f5f9" : "none"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: `${STAGE_COLORS[item.stage]}18`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                    }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: STAGE_COLORS[item.stage] || TEAL }} />
                    </span>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: DARK }}>{item.label}</p>
                      <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>{item.count} lead{item.count !== 1 ? "s" : ""} in range</p>
                    </div>
                  </div>
                  <span style={{ fontSize: 17, fontWeight: 800, color: item.avgDays > 0 ? dayColor(item.avgDays) : "#94a3b8", minWidth: 48, textAlign: "right" }}>
                    {fmtDays(item.avgDays)}
                  </span>
                </div>
              ))}
              <p style={{ margin: "14px 0 0", fontSize: 11, color: "#94a3b8", borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
                ● &lt; 1d good &nbsp;● 1–5d moderate &nbsp;● &gt; 5d needs attention
              </p>
            </>
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No active leads in this date range.</p>
          )}
        </Card>
      </div>

      {/* Footer */}
      <div className="px-3" style={{ paddingTop: 0, paddingBottom: 24, textAlign: "center" }}>
        <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
          Showing data for {dateRange[0]?.format("D MMM YYYY")} – {dateRange[1]?.format("D MMM YYYY")} · Updates when date range changes
        </p>
      </div>
        </div>
      </div>
    </div>
  );
};

export default CrmDashboard;
